import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import {
  MdDelete,
  MdDescription,
  MdPictureAsPdf,
  MdCheckCircle,
  MdError,
  MdSync,
  MdChevronLeft,
  MdChevronRight,
} from 'react-icons/md';
import toast from 'react-hot-toast';
import axios from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import ConfirmModal from './ConfirmModal';
import type { Document, DocumentStatus, KbScope } from '../types';

const SCOPE_OPTIONS: { value: KbScope; label: string }[] = [
  { value: 'both', label: 'Chat + Email' },
  { value: 'chat', label: 'Chat only' },
  { value: 'email', label: 'Email only' },
];

interface DocumentListProps {
  documents: Document[];
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const SYNC_STATUS: Record<
  DocumentStatus,
  { label: string; icon: React.ReactNode; className: string }
> = {
  processing: {
    label: 'Processing',
    icon: <MdSync className="animate-spin" size={14} />,
    className: 'bg-yellow-100 text-yellow-700',
  },
  completed: {
    label: 'Synced',
    icon: <MdCheckCircle size={14} />,
    className: 'bg-green-100 text-green-700',
  },
  failed: {
    label: 'Failed',
    icon: <MdError size={14} />,
    className: 'bg-red-100 text-red-700',
  },
};

export default function DocumentList({
  documents,
  page,
  totalPages,
  onPageChange,
}: DocumentListProps) {
  const [deleteTarget, setDeleteTarget] = useState<Document | null>(null);
  const queryClient = useQueryClient();

  // Poll for processing documents
  const hasProcessing = documents.some((doc) => doc.status === 'processing');

  useEffect(() => {
    if (!hasProcessing) return;

    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    }, 3000);

    return () => clearInterval(interval);
  }, [hasProcessing, queryClient]);

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return axios.delete(endpoints.documents.delete(id));
    },
    onSuccess: () => {
      toast.success('Document deleted');
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setDeleteTarget(null);
    },
    onError: () => {
      setDeleteTarget(null);
    },
  });

  const handleDelete = () => {
    if (!deleteTarget || deleteMutation.isPending) return;
    deleteMutation.mutate(deleteTarget.id);
  };

  const scopeMutation = useMutation({
    mutationFn: async ({ id, kb_scope }: { id: number; kb_scope: KbScope }) => {
      return axios.patch(endpoints.documents.updateScope(id), { kb_scope });
    },
    onSuccess: () => {
      toast.success('Document scope updated');
      queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: () => {
      // The select reads from cache, so a failure silently snaps it back and
      // the change looks like it applied to nothing.
      toast.error(
        'Could not change scope — the document may still be processing',
      );
    },
  });

  const getTypeIcon = (type: string) => {
    return type === 'pdf' ? (
      <MdPictureAsPdf className="text-red-500" size={20} />
    ) : (
      <MdDescription className="text-blue-500" size={20} />
    );
  };

  return (
    <div>
      {documents.length === 0 && page === 1 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 py-8 text-center">
          <MdDescription size={32} className="mx-auto mb-2 text-gray-300" />
          <p className="text-sm text-gray-500">No documents uploaded yet</p>
        </div>
      ) : (
        <>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Type
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Used in
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                    Uploaded
                  </th>
                  <th className="px-4 py-2 text-right text-xs font-medium uppercase text-gray-500">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {documents.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {getTypeIcon(doc.document_type)}
                        <a
                          href={`${import.meta.env.VITE_BACKEND_URL}${endpoints.documents.download(doc.id)}`}
                          download
                          className="text-sm text-gray-900 hover:text-blue-600 hover:underline"
                        >
                          {doc.title}
                        </a>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium uppercase text-gray-600">
                        {doc.document_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={doc.kb_scope}
                        onChange={(e) =>
                          scopeMutation.mutate({
                            id: doc.id,
                            kb_scope: e.target.value as KbScope,
                          })
                        }
                        disabled={
                          doc.status === 'processing' || scopeMutation.isPending
                        }
                        title={
                          doc.status === 'processing'
                            ? 'Cannot change scope while processing'
                            : 'Change which agents can use this document'
                        }
                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {SCOPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${SYNC_STATUS[doc.status].className}`}
                      >
                        {SYNC_STATUS[doc.status].icon}
                        {SYNC_STATUS[doc.status].label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {format(new Date(doc.created_at), 'd MMM yyyy')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setDeleteTarget(doc)}
                        disabled={doc.status === 'processing'}
                        className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                        title={
                          doc.status === 'processing'
                            ? 'Cannot delete while processing'
                            : 'Delete document'
                        }
                      >
                        <MdDelete size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => onPageChange(page - 1)}
                  disabled={page === 1}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <MdChevronLeft size={18} />
                  Previous
                </button>
                <button
                  onClick={() => onPageChange(page + 1)}
                  disabled={page === totalPages}
                  className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                  <MdChevronRight size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      <ConfirmModal
        isOpen={deleteTarget !== null}
        title="Delete Document"
        message={`Are you sure you want to delete "${deleteTarget?.title}"? The chatbot will no longer be able to use this document to answer questions.`}
        confirmLabel="Delete"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
        destructive
      />
    </div>
  );
}
